import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { Drawer, TextField } from "@outpave/ds";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import {
  getEmbeddedOriginProblem,
  MAX_EMBEDDED_ORIGINS,
  type EmbeddedOriginProblem,
} from "utils/embedded/embeddedOrigin";

interface EmbeddedOriginDrawerProps {
  open: boolean;
  allowed: string[];
  onClose: () => void;
  /** Saves the origin; rejects to keep the drawer open for another try. */
  onAdd: (origin: string) => Promise<void>;
}

/** Adds one origin to the list allowed to frame embedded components. */
const EmbeddedOriginDrawer: React.FC<EmbeddedOriginDrawerProps> = ({
  open,
  allowed,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [origin, setOrigin] = useState("");
  const [problem, setProblem] = useState<EmbeddedOriginProblem | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const problemText: Record<EmbeddedOriginProblem, string> = {
    invalid: t("EmbeddedOriginInvalid"),
    duplicate: t("EmbeddedOriginDuplicate"),
    limit: t("EmbeddedOriginsLimit", { count: MAX_EMBEDDED_ORIGINS }),
  };

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    origin.trim() !== "",
    onClose,
  );

  const handleClose = () => {
    if (submitting) return;
    requestLeave();
  };

  const handleSubmit = async () => {
    const candidate = origin.trim();
    const found = getEmbeddedOriginProblem(candidate, allowed);
    setProblem(found);
    if (found || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(candidate);
      setOrigin("");
      onClose();
    } catch {
      // The caller reported it; the drawer stays open with the value to retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={() => {
          setOrigin("");
          setProblem(null);
        }}
        title={t("Add origin")}
        loading={submitting}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={handleSubmit}
              disabled={submitting || !origin.trim()}
            >
              {t("Add origin")}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("EmbeddedComponentsDescription")}
        </Typography>
        <TextField
          label={t("Origin")}
          placeholder={t("EmbeddedOriginPlaceholder")}
          value={origin}
          onChange={(event) => {
            setOrigin(event.target.value);
            setProblem(null);
          }}
          errorText={problem ? problemText[problem] : undefined}
          inputProps={{ inputMode: "url", spellCheck: false }}
          fullWidth
          required
          disabled={submitting}
        />
      </Drawer>
      {dialog}
    </>
  );
};

export default EmbeddedOriginDrawer;
